import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean, useSetState } from 'react-use'
import { v4 as uuid4 } from 'uuid'

import {
  DefaultVariableResult,
  VariableActionOption,
  VariableResultCustomText,
  VariableResultGlobalVariable,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import {
  ChevronRight,
  Date16,
  NormalText16,
  Numfield16,
  PersonalCard16,
  Plus16,
  Replace16,
  Search16,
  SpaceBar24,
  Text16,
  Textfield,
} from '@makyoapp/makyo-icon/icon'
import { Button, ButtonColor, DD, Modal, ModalGridFooter, SimpleInput } from '@makyoapp/makyo-ui'

import { MarkdownEditor } from '@/lib/markdown'

import { hasVariableSelectFeatureFlag } from './types'
import VariableActionOutput from './VariableActionOutput'
import VariableAvatar from './VariableAvatar'
import VariableColumn from './VariableColumn'
import VariableColumnData from './VariableColumnData'
import VariableCurrentListItem from './VariableCurrentListItem'
import VariableCurrentRow from './VariableCurrentRow'
import VariableDateTime from './VariableDateTime'
import VariableFile from './VariableFile'
import VariableGlobalVariables from './VariableGlobalVariables'
import VariableIcon from './VariableIcon'
import VariableImage from './VariableImage'
import VariableMenu from './VariableMenu'
import VariableSelectPageCover from './VariablePageCover'
import VariableProfileData from './VariableProfileData'
import { useVariableSelectContext } from './VariableSelectContext'
import VariableUniqueID from './VariableSelectUniqueID'
import VariableSessionID from './VariableSessionID'
import VariableSingleCell from './VariableSingleCell'
import { VariableWrapper } from './VariableWrapper'

// TODO (ACTION-WORKFLOWS): REFACTOR THIS
// Refactor to separate between variable select for normal and extended actions

function VariableSelectColumn() {
  const { multiple, onChange, variableColumnOptions, setOpen, value, tableId, id } = useVariableSelectContext()

  return (
    <VariableColumn
      tableId={tableId}
      id={`${id}-column`}
      columnTypes={variableColumnOptions.columnTypes}
      excludeColIds={variableColumnOptions.excludeColIds}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectCurrentListItem() {
  const { multiple, onChange, variableColumnOptions, setOpen, value, tableId, id } = useVariableSelectContext()

  return (
    <VariableCurrentListItem
      tableId={tableId}
      id={`${id}-current-list-item`}
      columnTypes={variableColumnOptions.columnTypes}
      excludeColIds={variableColumnOptions.excludeColIds}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectCurrentRow() {
  const { multiple, onChange, variableColumnOptions, setOpen, value, id } = useVariableSelectContext()
  return (
    <VariableCurrentRow
      id={`${id}-current-row`}
      columnTypes={variableColumnOptions.columnTypes}
      excludeColIds={variableColumnOptions.excludeColIds}
      onChange={(newVal) => {
        const currentValue = Array.isArray(value) ? value : []
        onChange(multiple ? [...(currentValue as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectColumnData() {
  const { multiple, onChange, variableColumnOptions, setOpen, value, forceUserSpecific, id, tableId } =
    useVariableSelectContext()

  return (
    <VariableColumnData
      id={`${id}-column-data`}
      tableId={tableId}
      forceUserSpecific={forceUserSpecific}
      columnTypes={variableColumnOptions.columnTypes}
      excludeColIds={variableColumnOptions.excludeColIds}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectProfileData() {
  const { id, setOpen, onChange, multiple, value, variableColumnOptions } = useVariableSelectContext()

  return (
    <VariableProfileData
      id={`${id}-profile-data`}
      columnTypes={variableColumnOptions.columnTypes}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectSessionID() {
  const { id, setOpen, onChange, multiple, value } = useVariableSelectContext()

  return (
    <VariableSessionID
      id={`${id}-session-id`}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectUniqueID() {
  const { id, setOpen, onChange, multiple, value } = useVariableSelectContext()
  return (
    <VariableUniqueID
      id={`${id}-unique-id`}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectActionOutputDD() {
  const { id, setOpen, onChange, options } = useVariableSelectContext()
  return (
    <VariableActionOutput
      id={id}
      onChange={(value) => {
        const theValue = {
          ...value,
          flag: VariableSelectFeatureFlag.ActionOutput,
        } as unknown as DefaultVariableResult
        onChange(theValue)
      }}
      onHide={() => setOpen(false)}
      options={options}
    />
  )
}

function VariableSelectSingleCell() {
  const { id, onChange, value, multiple, variableColumnOptions, setOpen } = useVariableSelectContext()

  return (
    <VariableSingleCell
      id={`${id}-single-cell`}
      columnTypes={variableColumnOptions.columnTypes}
      onChange={(newVal) => {
        onChange(multiple ? [...(value as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
      excludeColIds={variableColumnOptions.excludeColIds}
    />
  )
}

function VariableSelectIcon() {
  const {
    setOpen,
    onChange,
    setPreventClose,
    value: contextValue,
    multiple,
    variableActionOptions,
  } = useVariableSelectContext()
  return (
    <VariableIcon
      actionType={variableActionOptions.type}
      onModalOpen={() => setPreventClose(true)}
      onModalClose={() => setPreventClose(false)}
      onChange={(newVal) => {
        setPreventClose(false)
        const currentValue = Array.isArray(contextValue) ? contextValue : []
        onChange(multiple ? [...(currentValue as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectAvatar() {
  const { id, setOpen, onChange, value: contextValue, multiple, variableActionOptions } = useVariableSelectContext()

  if (!variableActionOptions || variableActionOptions.type === VariableActionOption.None) {
    return (
      <button
        className='gap-2 sdd-item'
        onClick={() => {
          onChange(
            multiple
              ? [
                  ...(contextValue as DefaultVariableResult[]),
                  {
                    id: uuid4(),
                    flag: VariableSelectFeatureFlag.Avatar,
                  },
                ]
              : { id: uuid4(), flag: VariableSelectFeatureFlag.Avatar }
          )
          setOpen(false)
        }}
      >
        <PersonalCard16 />
        <div>
          <Trans>Avatar</Trans>
        </div>
      </button>
    )
  }

  return (
    <VariableAvatar
      id={`${id}-avatar`}
      actionType={variableActionOptions.type}
      onChange={(newVal) => {
        onChange(multiple ? [...(contextValue as DefaultVariableResult[]), newVal] : newVal)
        setOpen(false)
      }}
    />
  )
}

function VariableRichText() {
  const { t } = useTranslation()

  const [showModal, setShowModal] = useBoolean(false)
  const [tempValue, setTempValue] = useState('')
  const { setOpen, onChange, value: contextValue, multiple } = useVariableSelectContext()

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Focus on the text area within the Markdown editor when show is true
  useEffect(() => {
    if (showModal && wrapperRef.current) {
      const textarea = wrapperRef.current.querySelector('textarea')
      textarea?.focus()
    }
  }, [showModal])

  return (
    <>
      <div
        role='button'
        className='mk-ddt sdd-item mk-ddi mk-dd-row flex-c w-full'
        onClick={() => {
          setShowModal(true)
        }}
      >
        <div className='flex-c w-full pointer-events-none'>
          <NormalText16 />
          <div className='pointer-events-none'>
            <Trans>Rich Text</Trans>
          </div>
        </div>
        <ChevronRight className='pointer-events-none' />
      </div>

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
        }}
        closeOnClickOutside={false}
        className='z-400!'
      >
        <Modal.Body noPadding>
          <div className='text-m-m min-h-[400px] min-w-[700px] flex-cc' ref={wrapperRef}>
            <MarkdownEditor
              height='400px'
              onChange={(value) => setTempValue(value ?? '')}
              value={tempValue}
              className='w-full'
            />
          </div>
        </Modal.Body>
        <ModalGridFooter
          onHide={() => {
            // setShowModal(false)
          }}
          onSubmit={() => {
            setShowModal(false)
            setOpen(false)
            onChange(
              multiple
                ? [
                    ...((contextValue as DefaultVariableResult[]) ?? []),
                    {
                      id: uuid4(),
                      flag: VariableSelectFeatureFlag.RichText,
                      value: tempValue,
                    },
                  ]
                : {
                    id: uuid4(),
                    flag: VariableSelectFeatureFlag.RichText,
                    value: tempValue,
                  }
            )
          }}
          cancelLabel={t('Cancel')}
          onCancel={() => {
            setShowModal(false)
          }}
        />
      </Modal>
    </>
  )
}

function VariableSelectImage() {
  const { onChange, multiple, value: contextValue, setOpen, id } = useVariableSelectContext()
  return (
    <VariableImage
      id={`${id}-im-mgr`}
      onChange={(v) => {
        onChange(
          multiple
            ? [
                ...((contextValue ?? []) as DefaultVariableResult[]),
                {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.Image,
                  value: v,
                },
              ]
            : {
                id: uuid4(),
                flag: VariableSelectFeatureFlag.Image,
                value: v,
              }
        )
      }}
      onHide={() => setOpen(false)}
    />
  )
}

function VariableSelectFile() {
  const { onChange, multiple, value: contextValue, setOpen, id } = useVariableSelectContext()
  return (
    <VariableFile
      id={`${id}-file-mgr`}
      onChange={(v) => {
        onChange(
          multiple
            ? [
                ...((contextValue ?? []) as DefaultVariableResult[]),
                {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.File,
                  value: v,
                },
              ]
            : {
                id: uuid4(),
                flag: VariableSelectFeatureFlag.File,
                value: v,
              }
        )
      }}
      onHide={() => setOpen(false)}
    />
  )
}

function VariableSpace() {
  const { onChange, setOpen, multiple, value } = useVariableSelectContext()
  return (
    <button
      className='gap-2 sdd-item'
      onClick={() => {
        onChange(
          multiple
            ? [
                ...(value as unknown as DefaultVariableResult[]),
                {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.Space,
                },
              ]
            : { id: uuid4(), flag: VariableSelectFeatureFlag.Space }
        )
        setOpen(false)
      }}
    >
      <SpaceBar24 width={16} height={16} />
      <div>
        <Trans>Space</Trans>
      </div>
    </button>
  )
}

function VariableSelectCustomText() {
  const { multiple, value, onChange, setOpen, inputRef, variableCustomTextOptions } = useVariableSelectContext()
  const { inputType } = variableCustomTextOptions
  const [show, setShow] = useBoolean(false)
  const [tempValue, setTempValue] = useSetState<VariableResultCustomText>({
    id: uuid4(),
    value: inputType === 'text' ? '' : 0,
    flag: VariableSelectFeatureFlag.CustomText,
  })

  const { t } = useTranslation()
  return multiple ? (
    <VariableWrapper
      title={<Trans>Enter Custom</Trans>}
      icon={inputType === 'number' ? <Numfield16 /> : <Textfield />}
      show={show}
      setShow={setShow}
      defaultWidth={false}
    >
      <div className='px-2 flex-c flex-col'>
        <SimpleInput
          type={inputType}
          className='min-w-0'
          value={tempValue.value}
          placeholder={inputType === 'number' ? t('Enter number') : t('Enter text')}
          onChange={(e) => {
            const val = e.currentTarget.value
            if (inputType === 'number') {
              const numVal = isNaN(Number(val)) ? (tempValue.value ?? 0) : val
              setTempValue({
                value: Number(numVal),
                flag: VariableSelectFeatureFlag.CustomText,
              })
            } else {
              setTempValue({
                value: val,
              })
            }
          }}
        />
        <Button
          className='w-full'
          onClick={() => {
            setShow(false)
            setOpen(false)
            onChange([...(value as DefaultVariableResult[]), tempValue])
          }}
        >
          <Trans>Submit</Trans>
        </Button>
      </div>
    </VariableWrapper>
  ) : (
    <button
      className='gap-2 sdd-item'
      onClick={async () => {
        onChange(tempValue)
        setOpen(false)
        inputRef.current?.focus()
      }}
    >
      {inputType === 'number' ? <Numfield16 /> : <Textfield />}
      <div>
        <Trans>Enter Value</Trans>
      </div>
    </button>
  )
}

function VariableSelectMenu() {
  const { setOpen, onChange, value: contextValue, multiple } = useVariableSelectContext()
  return (
    <VariableMenu
      onChange={(newValue) => {
        onChange(multiple ? [...((contextValue ?? []) as DefaultVariableResult[]), newValue] : newValue)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectDateTime() {
  const { setOpen, onChange, value: contextValue, multiple } = useVariableSelectContext()
  return (
    <VariableDateTime
      onChange={(newValue) => {
        onChange(multiple ? [...((contextValue ?? []) as DefaultVariableResult[]), newValue] : newValue)
        setOpen(false)
      }}
    />
  )
}

function VariableSelectCurrentDateTime() {
  const { setOpen, onChange, value, multiple } = useVariableSelectContext()
  return (
    <button
      className='gap-2 sdd-item'
      onClick={() => {
        onChange(
          multiple
            ? [...(value as DefaultVariableResult[]), { id: uuid4(), flag: VariableSelectFeatureFlag.CurrentDateTime }]
            : { id: uuid4(), flag: VariableSelectFeatureFlag.CurrentDateTime }
        )
        setOpen(false)
      }}
    >
      <Date16 />
      <div>
        <Trans>Current Date & Time</Trans>
      </div>
    </button>
  )
}

function VariableSelectPageTitle() {
  const { id, setOpen, onChange, multiple, value } = useVariableSelectContext()
  return (
    <button
      id={`${id}-page-title`}
      className='gap-2 sdd-item'
      onClick={() => {
        onChange(
          multiple
            ? [
                ...(value as unknown as DefaultVariableResult[]),
                {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.PageTitle,
                },
              ]
            : { id: uuid4(), flag: VariableSelectFeatureFlag.PageTitle }
        )
        setOpen(false)
      }}
    >
      <Text16 />
      <div>
        <Trans>Page Title</Trans>
      </div>
    </button>
  )
}

function VariableSelectGlobalVariable() {
  const { id, value, setOpen, onChange, options } = useVariableSelectContext()
  return (
    <VariableGlobalVariables
      id={id}
      value={value as VariableResultGlobalVariable}
      onChange={(value) => {
        const theValue = {
          ...value,
          flag: VariableSelectFeatureFlag.GlobalVariable,
        } as unknown as DefaultVariableResult
        onChange(theValue)
      }}
      onHide={() => setOpen(false)}
      options={options}
    />
  )
}

// Default order for flags when flagOrder is not provided
const DEFAULT_FLAG_ORDER: VariableSelectFeatureFlag[] = [
  VariableSelectFeatureFlag.CustomText,
  VariableSelectFeatureFlag.CurrentRow,
  VariableSelectFeatureFlag.CurrentListItem,
  VariableSelectFeatureFlag.Column,
  VariableSelectFeatureFlag.ColumnData,
  VariableSelectFeatureFlag.SingleCell,
  VariableSelectFeatureFlag.ProfileData,
  VariableSelectFeatureFlag.Icon,
  VariableSelectFeatureFlag.Avatar,
  VariableSelectFeatureFlag.RichText,
  VariableSelectFeatureFlag.Menu,
  VariableSelectFeatureFlag.File,
  VariableSelectFeatureFlag.Image,
  VariableSelectFeatureFlag.PageCover,
  VariableSelectFeatureFlag.SearchBox,
  VariableSelectFeatureFlag.Space,
  VariableSelectFeatureFlag.DateTime,
  VariableSelectFeatureFlag.CurrentDateTime,
  VariableSelectFeatureFlag.PageTitle,
  VariableSelectFeatureFlag.SessionID,
  VariableSelectFeatureFlag.UniqueID,
  VariableSelectFeatureFlag.ActionOutput,
]

// Mapping flag to component render function
type FlagRenderConfig = {
  component: React.ReactNode
  requiresTableId?: boolean
}

export default function VariableSelectDropdown() {
  const {
    id,
    icon,
    featureFlags,
    open,
    setOpen,
    onChange,
    value,
    multiple,
    preventClose,
    limitType,
    tableId,
    flagOrder,
  } = useVariableSelectContext()

  const counters = useMemo(() => {
    const r: Record<number, number> = {}
    if (multiple && Array.isArray(value)) {
      for (const v of value) {
        if (!r[v.flag]) {
          r[v.flag] = 1
        } else {
          r[v.flag] += 1
        }
      }
    }
    return r
  }, [value, multiple])

  const hasReachedLimit = useCallback(
    (flag: VariableSelectFeatureFlag) => {
      const limit = limitType[flag]
      if (!limit || !multiple) {
        return false
      }
      const currentCount = counters[flag] ?? 0
      return currentCount >= limit
    },
    [counters, limitType, multiple]
  )

  const shouldRenderFlag = useCallback(
    (flag: VariableSelectFeatureFlag) => {
      if (!open) return false
      if (hasReachedLimit(flag)) return false
      if (!hasVariableSelectFeatureFlag(featureFlags, flag)) return false
      return true
    },
    [open, hasReachedLimit, featureFlags]
  )

  // Get ordered flags (custom order or default)
  const orderedFlags = useMemo(() => {
    if (!flagOrder || flagOrder.length === 0) {
      return DEFAULT_FLAG_ORDER
    }

    // Use custom order, but append any flags not in the custom order at the end
    const customOrderSet = new Set(flagOrder)
    const remainingFlags = DEFAULT_FLAG_ORDER.filter((flag) => !customOrderSet.has(flag))
    return [...flagOrder, ...remainingFlags]
  }, [flagOrder])

  // Create mapping of flag to component
  const flagComponents = useMemo(() => {
    const map: Record<VariableSelectFeatureFlag, React.ReactNode> = {
      [VariableSelectFeatureFlag.CustomText]: shouldRenderFlag(VariableSelectFeatureFlag.CustomText) ? (
        <VariableSelectCustomText />
      ) : null,
      [VariableSelectFeatureFlag.CurrentRow]: shouldRenderFlag(VariableSelectFeatureFlag.CurrentRow) ? (
        <VariableSelectCurrentRow />
      ) : null,
      [VariableSelectFeatureFlag.CurrentListItem]: shouldRenderFlag(VariableSelectFeatureFlag.CurrentListItem) ? (
        <VariableSelectCurrentListItem />
      ) : null,
      [VariableSelectFeatureFlag.Column]: shouldRenderFlag(VariableSelectFeatureFlag.Column) ? (
        <VariableSelectColumn />
      ) : null,
      [VariableSelectFeatureFlag.ColumnData]: shouldRenderFlag(VariableSelectFeatureFlag.ColumnData) ? (
        <VariableSelectColumnData />
      ) : null,
      [VariableSelectFeatureFlag.SingleCell]: shouldRenderFlag(VariableSelectFeatureFlag.SingleCell) ? (
        <VariableSelectSingleCell />
      ) : null,
      [VariableSelectFeatureFlag.ProfileData]: shouldRenderFlag(VariableSelectFeatureFlag.ProfileData) ? (
        <VariableSelectProfileData />
      ) : null,
      [VariableSelectFeatureFlag.Icon]: shouldRenderFlag(VariableSelectFeatureFlag.Icon) ? (
        <VariableSelectIcon />
      ) : null,
      [VariableSelectFeatureFlag.Avatar]: shouldRenderFlag(VariableSelectFeatureFlag.Avatar) ? (
        <VariableSelectAvatar />
      ) : null,
      [VariableSelectFeatureFlag.RichText]: shouldRenderFlag(VariableSelectFeatureFlag.RichText) ? (
        <VariableRichText />
      ) : null,
      [VariableSelectFeatureFlag.Menu]: shouldRenderFlag(VariableSelectFeatureFlag.Menu) ? (
        <VariableSelectMenu />
      ) : null,
      [VariableSelectFeatureFlag.File]: shouldRenderFlag(VariableSelectFeatureFlag.File) ? (
        <VariableSelectFile />
      ) : null,
      [VariableSelectFeatureFlag.Image]: shouldRenderFlag(VariableSelectFeatureFlag.Image) ? (
        <VariableSelectImage />
      ) : null,
      [VariableSelectFeatureFlag.PageCover]: shouldRenderFlag(VariableSelectFeatureFlag.PageCover) ? (
        <VariableSelectPageCover />
      ) : null,
      [VariableSelectFeatureFlag.SearchBox]: shouldRenderFlag(VariableSelectFeatureFlag.SearchBox) ? (
        <button
          className='gap-2 sdd-item'
          onClick={() => {
            onChange(
              multiple
                ? [
                    ...(value as unknown as DefaultVariableResult[]),
                    {
                      id: uuid4(),
                      flag: VariableSelectFeatureFlag.SearchBox,
                    },
                  ]
                : { id: uuid4(), flag: VariableSelectFeatureFlag.SearchBox }
            )
            setOpen(false)
          }}
        >
          <Search16 />
          <div>
            <Trans>Search Box</Trans>
          </div>
        </button>
      ) : null,
      [VariableSelectFeatureFlag.Space]: shouldRenderFlag(VariableSelectFeatureFlag.Space) ? <VariableSpace /> : null,
      [VariableSelectFeatureFlag.DateTime]: shouldRenderFlag(VariableSelectFeatureFlag.DateTime) ? (
        <VariableSelectDateTime />
      ) : null,
      [VariableSelectFeatureFlag.CurrentDateTime]: shouldRenderFlag(VariableSelectFeatureFlag.CurrentDateTime) ? (
        <VariableSelectCurrentDateTime />
      ) : null,
      [VariableSelectFeatureFlag.PageTitle]: shouldRenderFlag(VariableSelectFeatureFlag.PageTitle) ? (
        <VariableSelectPageTitle />
      ) : null,
      [VariableSelectFeatureFlag.SessionID]: shouldRenderFlag(VariableSelectFeatureFlag.SessionID) ? (
        <VariableSelectSessionID />
      ) : null,
      [VariableSelectFeatureFlag.UniqueID]: shouldRenderFlag(VariableSelectFeatureFlag.UniqueID) ? (
        <VariableSelectUniqueID />
      ) : null,
      [VariableSelectFeatureFlag.ActionOutput]: shouldRenderFlag(VariableSelectFeatureFlag.ActionOutput) ? (
        <VariableSelectActionOutputDD />
      ) : null,
    } as Record<VariableSelectFeatureFlag, React.ReactNode>

    return map
  }, [shouldRenderFlag, multiple, onChange, setOpen, value])

  return (
    <DD
      menuAlwaysRender={false}
      id={`${id}-var-select-dd`}
      toggleNoButton
      menuClassName='w-[220px] z-302'
      menuWrapperClassName='sdd-menu-subc text-m-m text-zinc-700 dark:text-slate-300'
      menuDefaultSize={false}
      placement={'bottom-start'}
      show={open}
      portalMenu
      onToggle={(nextShow, meta) => {
        const evt = meta.originalEvent
        const tgt = evt?.target as HTMLElement
        if (
          tgt?.closest('.sdd-search-wrap') ||
          tgt?.closest('.mk-modal-base') ||
          tgt?.className?.includes?.('search-input') ||
          tgt?.tagName === 'INPUT'
        ) {
          return
        }

        if (!preventClose) {
          setOpen(nextShow)
        }
      }}
      toggle={(active, toggleProps) => {
        return (
          <Button
            noPadding
            colorStyle={ButtonColor.Primary}
            defaultSize={false}
            className='w-6 h-6'
            icon={(icon ?? multiple) ? <Plus16 /> : value === undefined ? <Plus16 /> : <Replace16 />}
            {...toggleProps}
            onClick={(e) => {
              setOpen(!open)
            }}
          />
        )
      }}
    >
      {/* Render flags in custom or default order */}
      {orderedFlags.map((flag) => {
        const component = flagComponents[flag]
        return component ? <React.Fragment key={flag}>{component}</React.Fragment> : null
      })}

      {/* GlobalVariable is always rendered at the end */}
      {open && <VariableSelectGlobalVariable />}
    </DD>
  )
}
